import React from "react"
import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../utils"
import { buttonBaseConfig } from "../button-base"

// =============================================================================
// Props
// =============================================================================
export type ButtonProps = {
  className?: string
} & Omit<ButtonPrimitive.Props, "className"> &
  VariantProps<typeof buttonStyles>

// =============================================================================
// Styles
// =============================================================================
export const buttonStyles = cva(
  cn(
    buttonBaseConfig.base,
    "disabled:cursor-not-allowed disabled:text-gray-600 disabled:[&_svg]:text-gray-600"
  ),
  {
    ...buttonBaseConfig,
    variants: {
      ...buttonBaseConfig.variants,
      variant: {
        primary: cn(
          buttonBaseConfig.variants.variant.primary,
          "disabled:bg-gray-100"
        ),
        brand: cn(
          buttonBaseConfig.variants.variant.brand,
          "disabled:bg-gray-100"
        ),
        secondary: cn(
          buttonBaseConfig.variants.variant.secondary,
          "disabled:bg-gray-100"
        ),
        outlined: cn(
          buttonBaseConfig.variants.variant.outlined,
          "disabled:inset-ring-gray-50 disabled:bg-white"
        ),
        tertiary: cn(
          buttonBaseConfig.variants.variant.tertiary,
          "disabled:bg-transparent"
        )
      }
    }
  }
)

// =============================================================================
// Component
// =============================================================================
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    { className, variant = "primary", size = "medium", ...props },
    ref
  ) {
    return (
      <ButtonPrimitive
        className={cn(buttonStyles({ variant, size, className }))}
        data-slot="button"
        ref={ref}
        {...props}
      />
    )
  }
)

Button.displayName = "Button"
